import { lazy } from "react";
import { createBrowserRouter } from "react-router";

import { FIRST_PASSWORD_PATH } from "@/routes/areas";
import { RequireAuth, RequireRole, RoleLanding } from "@/routes/guards";

/* Route-level code splitting: the admin bundle is never downloaded by a
   member, and the login page does not pull in the whole application. */
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const FirstPasswordPage = lazy(() => import("@/features/auth/pages/FirstPasswordPage"));
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const ClientLayout = lazy(() => import("@/layouts/ClientLayout"));
const VerifierLayout = lazy(() => import("@/layouts/VerifierLayout"));
const DashboardPage = lazy(() => import("@/features/admin/pages/DashboardPage"));
const UsersPage = lazy(() => import("@/features/admin/pages/UsersPage"));
const MemberDashboardPage = lazy(() => import("@/features/tickets/pages/MemberDashboardPage"));
const ScannerPage = lazy(() => import("@/features/verify/pages/ScannerPage"));
const ScanPage = lazy(() => import("@/features/verify/pages/ScanPage"));
const ForbiddenPage = lazy(() => import("@/pages/ForbiddenPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

/**
 * The route table.
 *
 * Guards are route elements rather than per-page checks, so a new page under
 * /admin inherits the role requirement by being nested there -- it cannot be
 * forgotten.
 */
export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/forbidden", element: <ForbiddenPage /> },

  {
    element: <RequireAuth />,
    children: [
      { path: "/", element: <RoleLanding /> },

      // Under no layout on purpose: this is a gate, so it gets no sidebar, no
      // tab bar and nowhere to click away to. Inside RequireAuth because
      // setting a password needs the session -- see the path check there,
      // which is what stops the guard redirecting it to itself.
      { path: FIRST_PASSWORD_PATH, element: <FirstPasswordPage /> },

      {
        path: "/app",
        element: (
          <RequireRole roles={["client"]}>
            <ClientLayout />
          </RequireRole>
        ),
        children: [
          // The member's one screen is their dashboard, so it is the index
          // route rather than a redirect to one.
          { index: true, element: <MemberDashboardPage /> },
        ],
      },

      {
        path: "/admin",
        element: (
          <RequireRole roles={["admin"]}>
            <AdminLayout />
          </RequireRole>
        ),
        children: [
          // The dashboard is the landing screen, so it is the index route
          // rather than a redirect to one.
          { index: true, element: <DashboardPage /> },
          { path: "users", element: <UsersPage /> },
        ],
      },

      {
        path: "/verify",
        element: (
          <RequireRole roles={["verifier"]}>
            <VerifierLayout />
          </RequireRole>
        ),
        children: [{ index: true, element: <ScannerPage /> }],
      },

      // Outside VerifierLayout on purpose. The camera wants the whole screen,
      // which AppShell's padded <main> will not give it, and tying the
      // camera's lifetime to this route means leaving the page releases it
      // without anybody having to remember to.
      {
        path: "/verify/scan",
        element: (
          <RequireRole roles={["verifier"]}>
            <ScanPage />
          </RequireRole>
        ),
      },
    ],
  },

  { path: "*", element: <NotFoundPage /> },
]);
